/**
 * i18n - Internationalization module for Paraludum
 * Supports: English (en) and Hungarian (hu)
 */

export type Language = 'en' | 'hu';

interface Translations {
  en: TranslationStrings;
  hu: TranslationStrings;
}

interface TranslationStrings {
  // Common
  common: {
    loading: string;
    error: string;
    submit: string;
    next: string;
    previous: string;
    ready: string;
    waiting: string;
    cancel: string;
    close: string;
    backToHome: string;
  };

  // Landing page (main game selection)
  landing: {
    title: string;
    subtitle: string;
    languageSwitch: string;
    credits: string;
    games: {
      whoSaidThat: {
        title: string;
        description: string;
        players: string;
      };
      theOne: {
        title: string;
        description: string;
        players: string;
      };
      twoTruths: {
        title: string;
        description: string;
        players: string;
      };
    };
  };

  // Home page
  home: {
    title: string;
    subtitle: string;
    gameDescription: string;
    gameDescriptionTitle: string;
    gameDescriptionText: string;
    gameDescriptionSteps: string;
    createGame: string;
    startGame: string;
    addPlayersDescription: string;
    playerNameLabel: string;
    addButton: string;
    languageSwitch: string; // Tooltip for language switcher
  };

  // Setup phase (player selection & ready)
  setup: {
    welcomeTitle: string;
    welcomeSubtitle: string;
    waitingSubtitle: string;
    selectName: string;
    readyButton: string;
    readyMessage: string;
    waitingMessage: string;
    playersLabel: string;
    nameTaken: string;
    progressLabel: string;
  };

  // Questions phase
  questions: {
    title: string;
    subtitle: string;
    inputLabel: string;
    inputPlaceholder: string;
    submitButton: string;
    submittedTitle: string;
    submittedMessage: string;
    waitingMessage: string;
    progressLabel: string;
  };

  // Answers phase
  answers: {
    title: string;
    subtitle: string;
    inputLabel: string;
    inputPlaceholder: string;
    submitButton: string;
    submittedTitle: string;
    submittedMessage: string;
    waitingMessage: string;
    progressLabel: string;
  };

  // Guessing phase
  guessing: {
    title: string;
    subtitle: string;
    questionLabel: string;
    selectPlayer: string;
    submitButton: string;
    progressLabel: string;
    previousButton: string;
    nextButton: string;
  };

  // Results phase
  results: {
    winnerTitle: string;
    winnerSubtitle: string;
    gameOverTitle: string;
    gameOverSubtitle: string;
    yourResultTitle: string;
    yourPosition: string;
    points: string;
    correctGuesses: string;
    timesGuessed: string;
    topThreeTitle: string;
    fullRankingsTitle: string;
    statisticsTitle: string;
    totalPlayers: string;
    totalQuestions: string;
    averageCorrect: string;
    hardestToGuess: string;
    easiestToGuess: string;
    mostConfused: string;
    timesGuessedSuffix: string;
    confusedTimes: string;
    newRoundButton: string;
    playersLabel: string;
  };

  // Footer
  footer: {
    version: string;
    copyright: string;
  };

  // Errors
  errors: {
    networkError: string;
    gameNotFound: string;
    invalidToken: string;
    nameTaken: string;
    failedToCreate: string;
    failedToJoin: string;
    failedToSubmit: string;
    failedToLoad: string;
    failedToRestart: string;
    selectPlayer: string;
    enterQuestion: string;
    enterAnswer: string;
    unexpectedError: string;
    gameDataMissing: string;
    resultsNotAvailable: string;
  };

  // The One game
  theone: {
    subtitle: string;
    gameDescriptionTitle: string;
    gameDescriptionText: string;
    gameDescriptionSteps: string;
    writingTitle: string;
    writingSubtitle: string;
    writingInputLabel: string;
    writingInputPlaceholder: string;
    writingSubmitButton: string;
    writingSubmittedTitle: string;
    writingSubmittedMessage: string;
    writingWaitingMessage: string;
    votingTitle: string;
    votingSubtitle: string;
    votingSubmitButton: string;
    votingSubmittedTitle: string;
    votingSubmittedMessage: string;
    votingWaitingMessage: string;
    finalTwoTitle: string;
    finalTwoMessage: string;
    eliminatedTitle: string;
    eliminatedMessage: string;
    winnerTitle: string;
    winnerSubtitle: string;
    survivedRounds: string;
    survived: string;
    eliminated: string;
    eliminatedInRound: string;
    totalVotes: string;
    allEntriesTitle: string;
    roundNumber: string;
    playerStatsTitle: string;
  };

  // Two Truths, One Lie game
  twoTruths: {
    subtitle: string;
    gameDescriptionTitle: string;
    gameDescriptionText: string;
    gameDescriptionSteps: string;
    writingTitle: string;
    writingSubtitle: string;
    firstTruthLabel: string;
    secondTruthLabel: string;
    lieLabel: string;
    statementPlaceholder: string;
    truthPlaceholder: string;
    liePlaceholder: string;
    writingSubmitButton: string;
    writingSubmittedTitle: string;
    writingSubmittedMessage: string;
    writingWaitingMessage: string;
    guessingTitle: string;
    guessingSubtitle: string;
    guessingInstruction: string;
    guessingProgressLabel: string;
    selectLie: string;
    nextButton: string;
    previousButton: string;
    guessingSubmitButton: string;
    guessingSubmittedTitle: string;
    guessingSubmittedMessage: string;
    guessingWaitingMessage: string;
    winnerTitle: string;
    winnerSubtitle: string;
    winnersTitle: string;
    yourScore: string;
    correctGuesses: string;
    timesGuessed: string;
    bestLiarTitle: string;
    mostHonestTitle: string;
    averageGuesses: string;
    averageCorrect: string;
    allStatementsTitle: string;
    statementTruth: string;
    statementLie: string;
    votesReceived: string;
    errorTwoTruthsOneLie: string;
    errorAlreadySubmitted: string;
    highestScore: string;
    bestLiar: string;
    mostHonest: string;
  };
}

const translations: Translations = {
  en: {
    common: {
      loading: 'Loading...',
      error: 'Error',
      submit: 'Submit',
      next: 'Next',
      previous: 'Previous',
      ready: 'Ready',
      waiting: 'Waiting',
      cancel: 'Cancel',
      close: 'Close',
      backToHome: 'Back to Home',
    },

    landing: {
      title: 'Paraludum',
      subtitle: 'Community-building online games',
      languageSwitch: 'Váltás magyarra',
      credits: 'Entirely AI-built human ideas in the cloud ⚡ Prompted by <a href="https://spooner.hu" target="_blank" rel="noopener noreferrer" style="color: var(--primary-color); text-decoration: none;">Alex Spooner</a>, founder of Parabonum.',
      games: {
        whoSaidThat: {
          title: 'Who Said That?',
          description: 'See answers and guess which player responded that way!',
          players: '3-10 players',
        },
        theOne: {
          title: 'The One',
          description: 'Write creative entries and vote! The last survivor wins!',
          players: '3-12 players',
        },
        twoTruths: {
          title: 'Two Truths, One Lie',
          description: 'Guess which statement is false! The sharpest player wins!',
          players: '3-12 players',
        },
      },
    },

    home: {
      title: 'Paraludum',
      subtitle: 'Who Said That?',
      gameDescription: 'What is Paraludum - Who Said That?',
      gameDescriptionTitle: '🎭 What is Paraludum - Who Said That?',
      gameDescriptionText: 'A game where players must recognize each other based on honest answers to questions asked anonymously.',
      gameDescriptionSteps: '📝 Everyone anonymously asks a question → 💬 Everyone honestly answers all questions → 🏆 Whoever recognizes the most players wins!',
      createGame: 'Create New Game',
      startGame: 'Start Game',
      addPlayersDescription: 'Enter the names of players who will participate in the game.',
      playerNameLabel: 'Player name',
      addButton: 'Add',
      languageSwitch: 'Váltás magyarra',
    },

    setup: {
      welcomeTitle: 'Welcome, {name}!',
      welcomeSubtitle: 'Waiting for everyone to be ready...',
      waitingSubtitle: 'Waiting for everyone to join...',
      selectName: 'Choose your name',
      readyButton: "I'm Ready!",
      readyMessage: "✅ You're ready! Waiting for others...",
      waitingMessage: 'Waiting for others to join...',
      playersLabel: 'Players:',
      nameTaken: 'This name is already taken',
      progressLabel: 'Ready',
    },

    questions: {
      title: 'Ask Your Question',
      subtitle: 'Think of a question you\'re curious about regarding the other players. No one will see that you asked this question! For example: "What\'s your favorite movie?" or "Where would you like to live?"',
      inputLabel: 'Your question',
      inputPlaceholder: 'e.g., What is your favorite food?',
      submitButton: 'Submit Question',
      submittedTitle: '✅ Question submitted!',
      submittedMessage: 'Waiting for everyone to submit their question...',
      waitingMessage: '{count} {players} haven\'t submitted their question yet',
      progressLabel: 'Question',
    },

    answers: {
      title: 'Answer Questions',
      subtitle: 'Answer every question honestly and the way you would actually act or think!',
      inputLabel: 'Your answer',
      inputPlaceholder: 'Write your answer...',
      submitButton: 'Submit Answer',
      submittedTitle: '✅ Answers submitted!',
      submittedMessage: 'Waiting for everyone to submit their answers...',
      waitingMessage: '{count} {players} haven\'t submitted their answers yet',
      progressLabel: '',
    },

    guessing: {
      title: 'Who Said That',
      subtitle: 'Look at the answers and guess which player responded this way!',
      questionLabel: 'Question',
      selectPlayer: 'Select a player',
      submitButton: 'Submit Guesses',
      progressLabel: '',
      previousButton: '← Previous',
      nextButton: 'Next →',
    },

    results: {
      winnerTitle: '🏆 Congratulations, {name}!',
      winnerSubtitle: 'You won! {winners}',
      gameOverTitle: '🎪 Game Over!',
      gameOverSubtitle: 'See how you did!',
      yourResultTitle: 'Your result',
      yourPosition: '#{position}',
      points: '{points} points',
      correctGuesses: 'correct guesses',
      timesGuessed: 'times guessed',
      topThreeTitle: '🏅 Top 3',
      fullRankingsTitle: '📊 Full Rankings',
      statisticsTitle: '📈 Statistics',
      totalPlayers: 'players',
      totalQuestions: 'questions',
      averageCorrect: 'average correct guesses',
      hardestToGuess: '🎭 Hardest to guess',
      easiestToGuess: '😊 Easiest to guess',
      mostConfused: '🤔 Most confused',
      timesGuessedSuffix: '× guessed correctly',
      confusedTimes: '× confused with each other',
      newRoundButton: '🔄 Start New Round',
      playersLabel: 'players',
    },

    footer: {
      version: 'Paraludum v{version}',
      copyright: 'All rights reserved ©',
    },

    errors: {
      networkError: 'Failed to connect to the server. Please check your internet connection.',
      gameNotFound: 'Game not found',
      invalidToken: 'Invalid token',
      nameTaken: 'This name is already taken',
      failedToCreate: 'Failed to create game',
      failedToJoin: 'Failed to join game',
      failedToSubmit: 'Failed to submit',
      failedToLoad: 'Failed to load',
      failedToRestart: 'Failed to restart game',
      selectPlayer: 'Please select a player',
      enterQuestion: 'Please enter a question',
      enterAnswer: 'Please enter an answer',
      unexpectedError: 'An unexpected error occurred',
      gameDataMissing: 'Game data is missing or corrupted',
      resultsNotAvailable: 'Results are not available',
    },

    theone: {
      subtitle: 'The One',
      gameDescriptionTitle: '🎯 What is Paraludum - The One?',
      gameDescriptionText: 'In this game, every entry is anonymous, and only one thing matters: will what you wrote touch someone? Each round is a new chance to gain a follower. Those who get picked stay; those who don\'t, are eliminated. The rounds continue until only one remains: The One.',
      gameDescriptionSteps: '✍️ Active players write entries → 🗳️ Everyone votes → ❌ Those without votes are eliminated → 🏆 The last remaining player wins!',
      writingTitle: 'Write Your Entry, {name}!',
      writingSubtitle: 'Craft an anonymous entry strong enough for at least one player to pick.',
      writingInputLabel: 'Your entry',
      writingInputPlaceholder: 'Write something creative...',
      writingSubmitButton: 'Submit Entry',
      writingSubmittedTitle: '✅ Entry submitted!',
      writingSubmittedMessage: 'Waiting for other active players to submit their entries...',
      writingWaitingMessage: '{count} more entries needed',
      votingTitle: 'Vote for the Best Entry, {name}!',
      votingSubtitle: 'Choose your favorite entry (you cannot vote for your own)',
      votingSubmitButton: 'Submit Vote',
      votingSubmittedTitle: '✅ Vote submitted!',
      votingSubmittedMessage: 'Waiting for all players to vote...',
      votingWaitingMessage: '{count} more votes needed',
      finalTwoTitle: 'Final Two!',
      finalTwoMessage: 'Only two active players remain. The eliminated players will decide the winner!',
      eliminatedTitle: 'You Were Eliminated',
      eliminatedMessage: 'You were eliminated in round {round}, but you can still vote!',
      winnerTitle: '🏆 Congratulations, {name}!',
      winnerSubtitle: 'You are The One!',
      survivedRounds: 'Survived {rounds} rounds',
      survived: 'Survived',
      eliminated: 'Eliminated',
      eliminatedInRound: 'Eliminated in round {round}',
      totalVotes: 'total votes',
      allEntriesTitle: 'All Entries',
      roundNumber: 'Round {round}',
      playerStatsTitle: 'Player Statistics',
    },

    twoTruths: {
      subtitle: 'Two Truths, One Lie',
      gameDescriptionTitle: '🙃 What is Paraludum - Two Truths, One Lie?',
      gameDescriptionText: 'A classic game where each player writes two true statements and one false statement about themselves. Other players must guess which statement is the lie!',
      gameDescriptionSteps: '✍️ Everyone writes 2 truths and 1 lie → 🔍 Everyone guesses which statements are lies → 🏆 The player with the most correct guesses wins!',
      writingTitle: 'Write Your Statements, {name}!',
      writingSubtitle: 'Write two true statements and one false statement about yourself. Make them believable!',
      firstTruthLabel: 'One truth',
      secondTruthLabel: 'Another truth',
      lieLabel: 'One lie',
      statementPlaceholder: 'Write a statement...',
      truthPlaceholder: 'Make it as unbelievable as possible',
      liePlaceholder: 'Make it as believable as possible',
      writingSubmitButton: 'Submit Statements',
      writingSubmittedTitle: '✅ Statements submitted!',
      writingSubmittedMessage: 'Waiting for everyone to submit their statements...',
      writingWaitingMessage: '{count} {players} haven\'t submitted yet',
      guessingTitle: 'Guess the Lies, {name}!',
      guessingSubtitle: 'Look at each player\'s statements and guess which one is the lie.',
      guessingInstruction: 'Review all statements from each player, then select which one you think is the lie.',
      guessingProgressLabel: '',
      selectLie: 'Which statement is the LIE?',
      nextButton: 'Next',
      previousButton: 'Previous',
      guessingSubmitButton: 'Submit Guesses',
      guessingSubmittedTitle: '✅ Guesses submitted!',
      guessingSubmittedMessage: 'Waiting for everyone to submit their guesses...',
      guessingWaitingMessage: '{count} {players} haven\'t guessed yet',
      winnerTitle: '🏆 Congratulations, {name}!',
      winnerSubtitle: 'You won with {score} correct guesses!',
      winnersTitle: '🏆 We have a tie!',
      yourScore: 'Your Score',
      correctGuesses: 'correct guesses',
      timesGuessed: 'people guessed your lie',
      bestLiarTitle: '🎭 Best Liar',
      mostHonestTitle: '😊 Most Honest',
      averageGuesses: 'Average correct guesses',
      averageCorrect: 'average correct guesses',
      allStatementsTitle: 'All Statements',
      statementTruth: '✅ Truth',
      statementLie: '❌ Lie',
      votesReceived: '{count} people guessed correctly',
      errorTwoTruthsOneLie: 'You must submit exactly 2 truths and 1 lie',
      errorAlreadySubmitted: 'You have already submitted your statements',
      highestScore: '{score} points',
      bestLiar: '🎭 Best Liar',
      mostHonest: '😊 Most Honest',
    },
  },

  hu: {
    common: {
      loading: 'Betöltés...',
      error: 'Hiba',
      submit: 'Beküldés',
      next: 'Következő',
      previous: 'Előző',
      ready: 'Kész',
      waiting: 'Várakozás',
      cancel: 'Mégse',
      close: 'Bezár',
      backToHome: 'Vissza a főoldalra',
    },

    landing: {
      title: 'Paraludum',
      subtitle: 'Közösségépítő online játékok',
      languageSwitch: 'Switch to English',
      credits: 'Teljes mértékben MI által épített emberi ötletek a felhőben ⚡ Promptolta <a href="https://spooner.hu" target="_blank" rel="noopener noreferrer" style="color: var(--primary-color); text-decoration: none;">Alex Spooner</a>, a Parabonum alapítója.',
      games: {
        whoSaidThat: {
          title: 'Ki mondta?',
          description: 'Nézd meg a válaszokat és találd ki, hogy melyik játékos válaszolt így!',
          players: '3-10 játékos',
        },
        theOne: {
          title: 'Az Egyetlen',
          description: 'Írj kreatív bejegyzéseket és szavazz! Az utolsó túlélő nyer!',
          players: '3-12 játékos',
        },
        twoTruths: {
          title: 'Két igaz, egy hamis',
          description: 'Találd ki, melyik állítás hamis! A legéleslátásúbb játékos nyer!',
          players: '3-12 játékos',
        },
      },
    },

    home: {
      title: 'Paraludum',
      subtitle: 'Ki mondta?',
      gameDescription: 'Mi a Paraludum - Ki mondta?',
      gameDescriptionTitle: '🎭 Mi a Paraludum - Ki mondta?',
      gameDescriptionText: 'Egy olyan játék ahol a név nélkül feltett kérdésekre adott őszinte válaszok alapján kell felismerni egymást a játékosoknak.',
      gameDescriptionSteps: '📝 Mindenki anonim feltesz egy kérdést → 💬 Mindenki őszintén válaszol minden kérdésre → 🏆 Aki a legtöbb játékost felismeri, nyer!',
      createGame: 'Új játék indítása',
      startGame: 'Játék indítása',
      addPlayersDescription: 'Add meg a játékosok neveit, akik részt vesznek a játékban.',
      playerNameLabel: 'Játékos neve',
      addButton: 'Hozzáad',
      languageSwitch: 'Switch to English',
    },

    setup: {
      welcomeTitle: 'Üdv, {name}!',
      welcomeSubtitle: 'Várunk, hogy mindenki készen álljon...',
      waitingSubtitle: 'Várunk, hogy mindenki csatlakozzon...',
      selectName: 'Válaszd ki a neved',
      readyButton: 'Készen állok!',
      readyMessage: '✅ Te készen állsz! Várunk a többiekre...',
      waitingMessage: 'Várunk a többiekre...',
      playersLabel: 'Játékosok:',
      nameTaken: 'Ez a név már foglalt',
      progressLabel: 'Készen',
    },

    questions: {
      title: 'Tedd fel a kérdésed',
      subtitle: 'Gondolj egy kérdést, amire kíváncsi vagy a többiekkel kapcsolatban. Senki nem fogja látni, hogy te tetted fel ezt a kérdést! Például: "Mi a kedvenc filmed?" vagy "Hol szeretnél élni?"',
      inputLabel: 'A kérdésed',
      inputPlaceholder: 'pl. Mi a kedvenc ételed?',
      submitButton: 'Kérdés beküldése',
      submittedTitle: '✅ Kérdés beküldve!',
      submittedMessage: 'Várunk, hogy mindenki beküldje a kérdését...',
      waitingMessage: '{count} {players} még nem küldte be a kérdését',
      progressLabel: 'Kérdés',
    },

    answers: {
      title: 'Válaszolj a kérdésekre',
      subtitle: 'Válaszolj minden kérdésre őszintén és úgy, ahogy te cselekednél vagy gondolkodnál!',
      inputLabel: 'A válaszod',
      inputPlaceholder: 'Írd le a válaszod...',
      submitButton: 'Válasz beküldése',
      submittedTitle: '✅ Válaszok beküldve!',
      submittedMessage: 'Várunk, hogy mindenki beküldje a válaszait...',
      waitingMessage: '{count} {players} még nem küldte be a válaszait',
      progressLabel: '',
    },

    guessing: {
      title: 'Ki mondta',
      subtitle: 'Nézd meg a válaszokat és találd ki, hogy melyik játékos válaszolt így!',
      questionLabel: 'Kérdés',
      selectPlayer: 'Válassz egy játékost',
      submitButton: 'Tippek beküldése',
      progressLabel: '',
      previousButton: '← Előző',
      nextButton: 'Következő →',
    },

    results: {
      winnerTitle: '🏆 Gratulálunk, {name}!',
      winnerSubtitle: 'Nyertél! {winners}',
      gameOverTitle: '🎪 Játék vége!',
      gameOverSubtitle: 'Nézd meg, hogy szerepeltél!',
      yourResultTitle: 'A te eredményed',
      yourPosition: '#{position}',
      points: '{points} pont',
      correctGuesses: 'helyes tipp',
      timesGuessed: 'rád találtak',
      topThreeTitle: '🏅 Top 3',
      fullRankingsTitle: '📊 Teljes rangsor',
      statisticsTitle: '📈 Statisztikák',
      totalPlayers: 'játékos',
      totalQuestions: 'kérdés',
      averageCorrect: 'átlag helyes tipp',
      hardestToGuess: '🎭 Legnehezebb kitalálható',
      easiestToGuess: '😊 Legkönnyebb kitalálható',
      mostConfused: '🤔 Legtöbbször összetévesztve',
      timesGuessedSuffix: '× találták el',
      confusedTimes: '× tévesztették össze őket',
      newRoundButton: '🔄 Új kör indítása',
      playersLabel: 'játékos',
    },

    footer: {
      version: 'Paraludum v{version}',
      copyright: 'Minden jog fenntartva ©',
    },

    errors: {
      networkError: 'Nem sikerült kapcsolódni a szerverhez. Kérlek ellenőrizd az internet kapcsolatodat.',
      gameNotFound: 'A játék nem található',
      invalidToken: 'Érvénytelen token',
      nameTaken: 'Ez a név már foglalt',
      failedToCreate: 'Nem sikerült létrehozni a játékot',
      failedToJoin: 'Nem sikerült csatlakozni a játékhoz',
      failedToSubmit: 'Nem sikerült beküldeni',
      failedToLoad: 'Nem sikerült betölteni',
      failedToRestart: 'Nem sikerült újraindítani a játékot',
      selectPlayer: 'Kérlek válassz egy játékost',
      enterQuestion: 'Kérlek írj be egy kérdést',
      enterAnswer: 'Kérlek írj be egy választ',
      unexpectedError: 'Váratlan hiba történt',
      gameDataMissing: 'A játék adatok hiányoznak vagy sérültek',
      resultsNotAvailable: 'Az eredmények nem elérhetőek',
    },

    theone: {
      subtitle: 'Az Egyetlen',
      gameDescriptionTitle: '🎯 Mi a Paraludum - Az Egyetlen?',
      gameDescriptionText: 'Ebben a játékban minden bejegyzés névtelen, és csak egyetlen dolog számít: vajon megérint-e valakit, amit írtál. Minden kör egy új esély arra, hogy kövessenek. Aki követőt kap, marad; aki nem, kiesik. A körök addig folytatódnak, amíg végül csak egy marad: az Egyetlen.',
      gameDescriptionSteps: '✍️ Aktív játékosok írnak bejegyzést → 🗳️ Mindenki szavaz → ❌ A szavazat nélküliek kiesnek → 🏆 Az utolsó bennmaradó játékos nyer!',
      writingTitle: 'Írd meg a bejegyzésed, {name}!',
      writingSubtitle: 'Fogalmazz meg egy névtelen bejegyzést, ami elég erős ahhoz, hogy legalább egy játékos kiválassza.',
      writingInputLabel: 'A bejegyzésed',
      writingInputPlaceholder: 'Írj valami kreatívat...',
      writingSubmitButton: 'Bejegyzés beküldése',
      writingSubmittedTitle: '✅ Bejegyzés beküldve!',
      writingSubmittedMessage: 'Várunk a többi aktív játékosra...',
      writingWaitingMessage: 'Még {count} bejegyzés hiányzik',
      votingTitle: 'Szavazz a legjobb bejegyzésre, {name}!',
      votingSubtitle: 'Válaszd ki a kedvenc bejegyzésed (a sajátodra nem szavazhatsz)',
      votingSubmitButton: 'Szavazat beküldése',
      votingSubmittedTitle: '✅ Szavazat beküldve!',
      votingSubmittedMessage: 'Várunk az összes játékosra...',
      votingWaitingMessage: 'Még {count} szavazat hiányzik',
      finalTwoTitle: 'Döntő!',
      finalTwoMessage: 'Már csak két aktív játékos maradt. A kiesettek döntik el, ki lesz a győztes!',
      eliminatedTitle: 'Kiestél',
      eliminatedMessage: 'A {round}. körben kiestél, de továbbra is szavazhatsz!',
      winnerTitle: '🏆 Gratulálunk, {name}!',
      winnerSubtitle: 'Te vagy Az Egyetlen!',
      survivedRounds: '{rounds} kört élt túl',
      survived: 'Túlélő',
      eliminated: 'Kiesett',
      eliminatedInRound: '{round}. körben',
      totalVotes: 'összes szavazat',
      allEntriesTitle: 'Összes bejegyzés',
      roundNumber: '{round}. kör',
      playerStatsTitle: 'Játékos statisztikák',
    },

    twoTruths: {
      subtitle: 'Két igaz, egy hamis',
      gameDescriptionTitle: '🙃 Mi a Paraludum - Két igaz, egy hamis?',
      gameDescriptionText: 'Egy klasszikus játék, ahol minden játékos ír magáról két igaz és egy hamis állítást. A többiek célja kitalálni, melyik az, ami hazugság!',
      gameDescriptionSteps: '✍️ Mindenki ír 2 igaz és 1 hamis állítást → 🔍 Mindenki tippel, melyik a hamis → 🏆 A legtöbbet helyesen tippelő játékos nyer!',
      writingTitle: 'Írd meg az állításaid, {name}!',
      writingSubtitle: 'Írj magadról két igaz és egy hamis állítást. Légy minél kreatívabb!',
      firstTruthLabel: 'Egy igazság',
      secondTruthLabel: 'Még egy igazság',
      lieLabel: 'Egy hazugság',
      statementPlaceholder: 'Írj egy állítást...',
      truthPlaceholder: 'Legyen minél hihetetlenebb',
      liePlaceholder: 'Legyen minél hihetőbb',
      writingSubmitButton: 'Állítások beküldése',
      writingSubmittedTitle: '✅ Állítások beküldve!',
      writingSubmittedMessage: 'Várunk, hogy mindenki beküldje az állításait...',
      writingWaitingMessage: '{count} {players} még nem küldte be',
      guessingTitle: 'Találd meg a hamis állításokat, {name}!',
      guessingSubtitle: 'Nézd meg minden játékos állításait és találd ki, melyik a hamis.',
      guessingInstruction: 'Tippeld meg, hogy szerinted melyik a hamis állítás.',
      guessingProgressLabel: '',
      selectLie: 'Melyik állítás a HAMIS?',
      nextButton: 'Következő',
      previousButton: 'Előző',
      guessingSubmitButton: 'Tippek beküldése',
      guessingSubmittedTitle: '✅ Tippek beküldve!',
      guessingSubmittedMessage: 'Várunk, hogy mindenki beküldje a tippjeit...',
      guessingWaitingMessage: '{count} {players} még nem tippelt',
      winnerTitle: '🏆 Gratulálunk, {name}!',
      winnerSubtitle: '{score} helyes tippel nyertél!',
      winnersTitle: '🏆 Döntetlen!',
      yourScore: 'A te eredményed',
      correctGuesses: 'helyes tipp',
      timesGuessed: 'ember találta ki a hazugságod',
      bestLiarTitle: '🎭 Legjobb hazudozó',
      mostHonestTitle: '😊 Legőszintébb',
      averageGuesses: 'Átlagos helyes tippek',
      averageCorrect: 'átlagos helyes tippek',
      highestScore: '{score} pont',
      bestLiar: '🎭 Legjobb hazudós',
      mostHonest: '😊 Legőszintébb',
      allStatementsTitle: 'Összes állítás',
      statementTruth: '✅ Igaz',
      statementLie: '❌ Hamis',
      votesReceived: '{count} ember találta ki',
      errorTwoTruthsOneLie: 'Pontosan 2 igaz és 1 hamis állítást kell beküldened',
      errorAlreadySubmitted: 'Már beküldted az állításaidat',
    },
  },
};

// Current language state
let currentLanguage: Language = 'en';

/**
 * Initialize language from localStorage or browser settings
 */
export function initLanguage(): Language {
  // 1. Check localStorage
  const saved = localStorage.getItem('paraludum_language');
  if (saved === 'en' || saved === 'hu') {
    currentLanguage = saved;
    return currentLanguage;
  }

  // 2. Check browser language
  const browserLang = navigator.language.toLowerCase();
  if (browserLang.startsWith('hu')) {
    currentLanguage = 'hu';
    localStorage.setItem('paraludum_language', 'hu');
    return currentLanguage;
  }

  // 3. Default to English
  currentLanguage = 'en';
  localStorage.setItem('paraludum_language', 'en');
  return currentLanguage;
}

/**
 * Get current language
 */
export function getCurrentLanguage(): Language {
  return currentLanguage;
}

/**
 * Set language and save to localStorage
 */
export function setLanguage(lang: Language): void {
  currentLanguage = lang;
  localStorage.setItem('paraludum_language', lang);
}

/**
 * Get translation by key path
 */
export function t(key: string, params?: Record<string, string | number>): string {
  const keys = key.split('.');
  let value: any = translations[currentLanguage];

  for (const k of keys) {
    if (value && typeof value === 'object') {
      value = value[k];
    } else {
      console.warn(`Translation key not found: ${key}`);
      return key;
    }
  }

  if (typeof value !== 'string') {
    console.warn(`Translation key is not a string: ${key}`);
    return key;
  }

  // Replace parameters
  if (params) {
    return value.replace(/\{(\w+)\}/g, (match, paramKey) => {
      return params[paramKey] !== undefined ? String(params[paramKey]) : match;
    });
  }

  return value;
}

/**
 * Get translation with plural support
 */
export function tp(count: number, singular: string, plural?: string): string {
  if (currentLanguage === 'hu') {
    // Hungarian doesn't have plural forms like English
    return singular;
  }

  // English plural
  return count === 1 ? singular : (plural || singular + 's');
}
